import { useEffect, useRef } from "react";
import { EnvironmentOverlay } from "./environment/EnvironmentOverlay";
import { LivingWater } from "./environment/LivingWater";
import { SceneBackground } from "./environment/SceneBackground";
import { useViewportProfile } from "./hooks/useViewportProfile";
import type { V2Scene } from "./types";
import "./styles/tokens.css";
import "./styles/experience.css";
import "./styles/cinema.css";
import "./styles/flash.css";

/**
 * `/share` 落地页（V5 版）。
 *
 * 票面上的二维码与外部分享链接都指向这里，所以它原本由 V1 的 SharePage 承担——
 * 那是另一套视觉语言（global.css 的圆角卡片 + 蓝灰配色），扫码进来会突然"换了个作品"。
 * 这里改成与九幕完全同一套语言：南湖背景 + 深墨书法标题 + 朱砂可点。
 *
 * 契约（tests/e2e/journey.spec.ts 会查）：`data-testid="share"` 必须可见，
 * 二维码 canvas 的 aria-label 与 width=176 不可改。
 * 不复用开场的盖章过场（.v2-boot），否则落地页要等动画才看得见内容。
 */
const SHARE_SCENE: V2Scene = "intro";

export function ShareEntry() {
  const profile = useViewportProfile();
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    let cancelled = false;
    void import("@/lib/qr").then(({ drawShareQr }) => {
      /* 正式分享地址优先；未配置时退回当前 origin，不把 localhost 写进最终材料 */
      const shareUrl = import.meta.env.VITE_PUBLIC_URL || window.location.origin;
      if (!cancelled && canvasRef.current) void drawShareQr(canvasRef.current, shareUrl);
    });
    return () => { cancelled = true; };
  }, []);

  return (
    <main className={"v2-experience v2-profile-" + profile + " v2-scene-intro v2-share-root"}>
      <SceneBackground scene={SHARE_SCENE} previousScene={null} profile={profile} />
      <LivingWater scene={SHARE_SCENE} profile={profile} pulse={false} />
      <EnvironmentOverlay scene={SHARE_SCENE} pulse={false} />
      <header className="v2-hud">
        <a className="v2-brand" href="/" aria-label="进入红船三问"><span>红船三问</span><small>AI时代的青年答案</small></a>
      </header>
      <div className="v2-film" aria-hidden="true"><div className="v2-grain" /><div className="v2-vignette" /></div>
      <div className="v2-letterbox" aria-hidden="true"><i /><i /></div>

      <section className="v2-share-page" data-testid="share">
        <p className="v2-place">SCAN TO BEGIN / 分享入口</p>
        <h1>把一个问题，<br /><span className="v2-accent">带到红船旁</span>。</h1>
        <div className="v2-share-qr">
          <canvas ref={canvasRef} width={176} height={176} aria-label="扫码进入红船三问" />
          <small>扫码开始你的三问</small>
        </div>
        <p className="v2-share-copy">
          从一个真实的问题出发，经过首创、奋斗、奉献三次选择，重新看见自己的回答，
          留下一件明天就能做到的小事，并收下属于你的红船青年船票。
        </p>
        <a className="v2-text-cta v2-in" href="/">开始体验 <span>→</span></a>
      </section>
    </main>
  );
}
